import React, { useState } from "react";
import { toast, Toaster } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { NoMedicalInfoNote, FormPrivacyNote } from "./FormPrivacyNote";

const REASONS = [
  "Hypertension clinic",
  "Cholesterol and lipid clinic",
  "Cardiovascular risk assessment",
  "Echocardiogram",
  "Other",
];

const URGENCY = ["Routine", "Soon (within 2 weeks)", "Urgent (within 5 working days)"];

export default function GPReferralForm() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    gp_name: "",
    practice: "",
    gp_email: "",
    gp_phone: "",
    patient_initials: "",
    patient_age: "",
    patient_phone: "",
    patient_email: "",
    reason: "",
    urgency: "Routine",
    summary: "",
    consent: false,
  });

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.consent) {
      setError("Please confirm patient consent before sending.");
      return;
    }
    setSubmitting(true);
    try {
      // Encode as a structured enquiry via the existing endpoint
      const reason = [
        `[GP REFERRAL · ${form.urgency}]`,
        `GP: ${form.gp_name} (${form.practice})`,
        `GP contact: ${form.gp_email} · ${form.gp_phone || "—"}`,
        `Patient: ${form.patient_initials}, age ${form.patient_age}`,
        `Patient contact: ${form.patient_phone || "—"} · ${form.patient_email || "—"}`,
        `Service requested: ${form.reason}`,
        ``,
        `Clinical details: to follow by secure email.`,
      ].join("\n");

      await api.post("/enquiries", {
        full_name: `GP Referral · ${form.patient_initials}`,
        email: form.gp_email,
        phone: form.gp_phone || form.patient_phone || "—",
        date_of_birth: "",
        reason,
        preferred_consultant: "",
        payment_type: "self_pay",
        preferred_times: form.urgency,
        consent: true,
      });
      setSubmitted(true);
      toast.success("Referral received — we will be back with you shortly.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="gp-referral-success" className="p-8 bg-cream-100 border border-bronze-light/40 rounded-md text-center">
        <div className="eyebrow">Referral received</div>
        <h3 className="font-serif text-3xl mt-3">Thank you.</h3>
        <p className="mt-4 text-ink-muted">We will be back with you shortly to confirm appointment options for your patient.</p>
      </div>
    );
  }

  const baseInput = "w-full rounded-sm border border-stone-300 bg-white px-4 py-3 text-sm font-sans focus:outline-none focus:ring-2 focus:ring-bronze-dark/40 focus:border-bronze-dark transition";
  const label = "block text-xs uppercase tracking-[0.18em] text-ink-muted font-medium mb-2";

  return (
    <form onSubmit={submit} data-testid="gp-referral-form" className="grid grid-cols-1 md:grid-cols-2 gap-5">
      <Toaster richColors position="top-right" />
      <NoMedicalInfoNote />
      <div className="md:col-span-2 eyebrow">Referring clinician</div>
      <div>
        <label htmlFor="gp-name" className={label}>GP name *</label>
        <input id="gp-name" required data-testid="gp-name-input" className={baseInput} value={form.gp_name} onChange={update("gp_name")} />
      </div>
      <div>
        <label htmlFor="gp-practice" className={label}>Practice *</label>
        <input id="gp-practice" required data-testid="gp-practice-input" className={baseInput} value={form.practice} onChange={update("practice")} />
      </div>
      <div>
        <label htmlFor="gp-email" className={label}>GP email *</label>
        <input id="gp-email" required type="email" data-testid="gp-email-input" className={baseInput} value={form.gp_email} onChange={update("gp_email")} />
      </div>
      <div>
        <label htmlFor="gp-phone" className={label}>GP phone *</label>
        <input id="gp-phone" required minLength={5} type="tel" data-testid="gp-phone-input" className={baseInput} value={form.gp_phone} onChange={update("gp_phone")} />
      </div>

      <div className="md:col-span-2 eyebrow mt-3">Patient</div>
      <div>
        <label htmlFor="patient-initials" className={label}>Patient initials *</label>
        <input id="patient-initials" required data-testid="patient-initials-input" className={baseInput} placeholder="e.g. J.D." value={form.patient_initials} onChange={update("patient_initials")} />
      </div>
      <div>
        <label htmlFor="patient-age" className={label}>Patient age *</label>
        <input id="patient-age" required type="number" min="0" max="120" data-testid="patient-age-input" className={baseInput} value={form.patient_age} onChange={update("patient_age")} />
      </div>
      <div>
        <label htmlFor="patient-phone" className={label}>Patient phone</label>
        <input id="patient-phone" type="tel" data-testid="patient-phone-input" className={baseInput} value={form.patient_phone} onChange={update("patient_phone")} />
      </div>
      <div>
        <label htmlFor="patient-email" className={label}>Patient email</label>
        <input id="patient-email" type="email" data-testid="patient-email-input" className={baseInput} value={form.patient_email} onChange={update("patient_email")} />
      </div>

      <div className="md:col-span-2 eyebrow mt-3">Referral</div>
      <div>
        <label htmlFor="referral-reason" className={label}>Service requested *</label>
        <select id="referral-reason" required data-testid="referral-reason-select" className={baseInput} value={form.reason} onChange={update("reason")}>
          <option value="">Please choose…</option>
          {REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      </div>
      <div>
        <label htmlFor="referral-urgency" className={label}>Urgency *</label>
        <select id="referral-urgency" required data-testid="referral-urgency-select" className={baseInput} value={form.urgency} onChange={update("urgency")}>
          {URGENCY.map((u) => <option key={u} value={u}>{u}</option>)}
        </select>
      </div>
      <p data-testid="referral-secure-note" className="md:col-span-2 text-sm text-ink-muted">
        Please don&apos;t enter clinical details here. Once we receive this form we&apos;ll reply with a secure way to send the referral letter and clinical information.
      </p>

      <div className="md:col-span-2 flex items-start gap-3 p-5 bg-cream-100 border border-bronze-light/40 rounded-sm">
        <input id="gp-consent" type="checkbox" data-testid="referral-consent-checkbox" checked={form.consent} onChange={update("consent")} className="mt-1 accent-bronze-dark" />
        <label htmlFor="gp-consent" className="text-sm text-ink-muted">
          I confirm the patient has consented to this referral and to their information being shared with Cambridge Cardiovascular Prevention Clinic for the purpose of arranging care.
        </label>
      </div>

      {error && <div className="md:col-span-2 text-sm text-destructive">{error}</div>}
      <div className="md:col-span-2 flex flex-wrap gap-4">
        <button disabled={submitting} type="submit" data-testid="gp-referral-submit" className="btn-primary disabled:opacity-60">
          {submitting ? "Sending…" : "Send referral"}
        </button>
      </div>
      <FormPrivacyNote className="md:col-span-2" />
    </form>
  );
}
